declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

// Fired once per user, right after their first baby profile is created during onboarding —
// this is the signup conversion event for the Google Ads campaign (see docs/marketing/decisions-log.md).
export function reportSignupConversion() {
  window.gtag?.("event", "conversion", {
    send_to: ["AW-17038297266/cI_tCNiKtt4cELKRv7w_", "G-QVKW833YB6"],
  });
}

// Fired every time the floating "Log activity" button is tapped, opening the log form.
// Plain GA4 engagement event (no send_to) — not wired as an Ads conversion since it fires repeatedly.
export function reportLogActivityClick() {
  window.gtag?.("event", "log_activity_click");
}

// Fired when an anonymous session becomes a real account (the "Save your data" register
// screen) — i.e. actual account creation, distinct from onboarding's baby-profile-creation
// conversion above. GA4's standard recommended event, not sent to Ads: the AW "Sign-up"
// conversion action is already tied to baby-profile creation, so firing it here too would
// double-count one user as two conversions.
export function reportAccountSignup(method: "google" | "password") {
  window.gtag?.("event", "sign_up", { method });
}

// Fired once each time the get-started/onboarding screen mounts (new visitor, or an existing
// user who deleted their only baby profile). App.tsx gives this screen its own "/get-started"
// URL, but GA4 only auto-tracks page_view on initial script load — SPA route changes need an
// explicit page_view event to show up as their own page rather than folding into "/".
export function reportOnboardingView() {
  window.gtag?.("event", "page_view", { page_path: "/get-started", page_title: "Get started" });
}

// Fired the moment a log event finishes writing to Firestore — the last step of the funnel
// that starts at reportLogActivityClick. Lets a GA4 funnel show whether users who open the
// form actually complete a save, not just whether they tapped the FAB.
export function reportLogSaveSuccess(logType: string) {
  window.gtag?.("event", "log_save_success", { log_type: logType });
}

// Fired when a log event fails to save — either a client-side validation bounce (e.g. sleep
// end before start) or a Firestore/Storage write throwing. `reason` is a short fixed bucket,
// not the raw error message, so it stays low-cardinality and free of any user-entered text.
export function reportLogSaveError(reason: string) {
  window.gtag?.("event", "log_save_error", { reason });
}
